import { type FunctionComponent } from "react";
import { CheckCircleFilled, TrophyFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Progress, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useNavigate } from "react-router-dom";

import {
  challengeHasPassMark,
  ChallengeParticipantStatus,
  type Challenge,
  type ChallengeScoreSnapshotEntity,
} from "@fitness-app/data-models/entities/Challenge";
import { challengeProgress } from "@fitness-app/data-models/domain/services/ChallengeProgress";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import TraineeSimulatorButton from "~/modules/Trainee/TraineeSimulator/TraineeSimulatorButton";
import { supabase } from "~/store/initializeStore";

type LeaderboardRow = ChallengeScoreSnapshotEntity & {
  trainee: Pick<Trainee, "id" | "firstName" | "lastName" | "email"> | null;
  participant: { status: string; completedAt: string | null } | null;
};

type Props = {
  challengeId: string;
};

const rankColors: Record<number, string> = {
  1: "#faad14",
  2: "#bfbfbf",
  3: "#d48806",
};

const ChallengeLeaderboardTable: FunctionComponent<Props> = ({ challengeId }) => {
  const navigate = useNavigate();
  const leaderboardQuery = useQuery({
    queryKey: ["challenge-leaderboard", challengeId],
    // Live-ish: poll while the tab is open and refetch on focus — no manual refresh needed.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_score_snapshot")
        .select(
          "*, trainee:traineeId!inner(id, firstName, lastName, email), participant:participantId(status, completedAt)",
        )
        .eq("challengeId", challengeId)
        .order("points", { ascending: false })
        .returns<LeaderboardRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // TRE-1577: pass-mark meta (goalType/passPoints) — same key as the detail page, so it dedupes.
  const challengeQuery = useQuery({
    queryKey: ["challenge-detail", challengeId],
    queryFn: async () => {
      const { data, error } = await supabase.from("challenge").select("*").eq("id", challengeId).single();
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const challenge = challengeQuery.data as Pick<Challenge, "goalType" | "passPoints"> | undefined;
  const hasPassMark = challenge ? challengeHasPassMark(challenge) : false;
  // TRE-1585 — leavers drop out of the ranking (stale snapshots are pruned by the trigger/sweep,
  // but filter here too so the table is immediately consistent) and the DISPLAYED rank is a live
  // dense rank over the remaining field — a leaver's place is reclaimed without waiting for
  // the recompute.
  const rows = (() => {
    const filtered = (leaderboardQuery.data ?? []).filter(
      (row) => row.participant?.status !== ChallengeParticipantStatus.Left,
    );
    let rank = 0;
    let previousPoints: number | null = null;
    return filtered.map((row) => {
      if (previousPoints === null || row.points !== previousPoints) {
        rank += 1;
        previousPoints = row.points;
      }
      return { ...row, rank };
    });
  })();
  const passedCount = rows.filter((row) => row.participant?.status === ChallengeParticipantStatus.Completed).length;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {hasPassMark ? (
        <Space size={8}>
          <Tag color="blue">Próg zaliczenia: {challenge?.passPoints} pkt</Tag>
          <Typography.Text type="secondary">
            Zaliczyło: <Typography.Text strong>{passedCount}</Typography.Text> z {rows.length}
          </Typography.Text>
        </Space>
      ) : null}
      <Table<LeaderboardRow>
        dataSource={rows}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={leaderboardQuery.isFetching}
        locale={{ emptyText: "Brak rankingu dla tego wyzwania" }}
        pagination={false}
      >
        <Table.Column<LeaderboardRow>
          title="#"
          dataIndex="rank"
          key="rank"
          width={70}
          render={(rank: number | null, _row, index) => {
            const position = rank ?? index + 1;
            return (
              <Space size={4}>
                {position <= 3 ? <TrophyFilled style={{ color: rankColors[position] }} /> : null}
                <Typography.Text strong>{position}</Typography.Text>
              </Space>
            );
          }}
        />
        <Table.Column<LeaderboardRow>
          title="Klientka"
          dataIndex="trainee"
          key="trainee"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Button
                type="link"
                className="px-0 h-auto text-left font-semibold"
                onClick={() => navigate(`/trainee/details/${row.traineeId}`)}
              >
                {row.trainee?.firstName} {row.trainee?.lastName}
              </Button>
              <Typography.Text type="secondary">{row.trainee?.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<LeaderboardRow>
          title="Punkty"
          dataIndex="points"
          key="points"
          sorter={(a, b) => a.points - b.points}
          render={(points: number) => <Typography.Text strong>{points} pkt</Typography.Text>}
        />
        {hasPassMark ? (
          <Table.Column<LeaderboardRow>
            title="Zaliczone"
            key="passed"
            render={(_, row) => {
              const isPassed = row.participant?.status === ChallengeParticipantStatus.Completed;
              const passPoints = challenge?.passPoints ?? 0;
              const percent = passPoints > 0 ? Math.min(100, Math.round((row.points / passPoints) * 100)) : 0;
              const surplus = row.points - passPoints;

              // Dorobek pokazujemy też po zaliczeniu progu. W wyzwaniu z rankingiem próg nie kończy
              // gry — dalej można zbierać punkty na podium — a sam znacznik „Zaliczone" gubił
              // informację, ile uczestniczka faktycznie ma.
              return (
                <Space size={8} wrap>
                  {isPassed ? (
                    <Tag color="green" icon={<CheckCircleFilled />}>
                      Zaliczone
                    </Tag>
                  ) : (
                    <Progress percent={percent} size="small" style={{ width: 120 }} showInfo={false} />
                  )}
                  <Typography.Text type="secondary">
                    {row.points} / {passPoints}
                    {isPassed && surplus > 0 ? ` (+${surplus})` : ""}
                  </Typography.Text>
                </Space>
              );
            }}
          />
        ) : null}
        <Table.Column<LeaderboardRow>
          title="Bonus za serię"
          dataIndex="streakBonus"
          key="streakBonus"
          render={(streakBonus: number) => `+${streakBonus} pkt`}
        />
        <Table.Column<LeaderboardRow>
          title="Bonus za kamienie milowe"
          dataIndex="milestoneBonus"
          key="milestoneBonus"
          render={(milestoneBonus: number) => `+${milestoneBonus} pkt`}
        />
        <Table.Column<LeaderboardRow>
          // "Postęp", not "Ukończone zadania": the big number counts a multi-date recording task once
          // per date, so it is not a task count — and naming the header after tasks while the line
          // below it also said "zadania" made the same word mean two things in one column. The unit
          // is deliberately unnamed here for the same reason `challengeProgressLabel` drops the noun
          // once dates are in play: the total mixes whole tasks with individual recording dates.
          title={
            <Tooltip title="Zadanie z wieloma terminami (np. nagranie na 12 dat) liczy się per termin, a nie jako jedno zadanie.">
              <span>Postęp</span>
            </Tooltip>
          }
          key="progress"
          render={(_, row) => {
            // The snapshot has carried per-date units since TRE-1828 and every client-facing surface
            // already reads them — this table was the last one still counting whole tasks, so a
            // nine-video task sat at "0 / 1" for the entire challenge.
            const progress = challengeProgress(row);

            if (!progress.countsDates) {
              return `${row.completedTasks} / ${row.totalTasks}`;
            }

            return (
              <Space direction="vertical" size={0}>
                <span>{`${progress.completed} / ${progress.total}`}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {`ukończone zadania: ${row.completedTasks} / ${row.totalTasks}`}
                </Typography.Text>
              </Space>
            );
          }}
        />
        <Table.Column<LeaderboardRow>
          title=""
          key="simulator"
          render={(_, row) => <TraineeSimulatorButton traineeId={row.traineeId} />}
        />
      </Table>
    </Space>
  );
};

export default ChallengeLeaderboardTable;
